import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { useAppContext } from '../../context/AppContext';
import { sendToLiveBackend, sendToMockBackend } from '../../services/api';
import ErrorBoundary from '../Common/ErrorBoundary';
import { Plot2D, Plot3D, VectorField } from '../Plotting';
import { preprocessDSLInput } from '../../utils/dslPreprocessor';

const LatexRenderer = () => {
  const { dslInput, latexOutput, setLatexOutput, isLiveMode, isProcessing, setIsProcessing } = useAppContext();
  const [error, setError] = useState('');

  // Memoize MathJax configuration to prevent recreation on every render
  const mathJaxConfig = useMemo(() => ({
    loader: { load: ['[tex]/html'] },
    tex: {
      packages: { '[+]': ['html'] },
      inlineMath: [['$', '$'], ['\\(', '\\)']],
      displayMath: [['$$', '$$'], ['\\[', '\\]']]
    },
    options: {
      menuOptions: {
        settings: {
          assistiveMml: true
        }
      }
    }
  }), []);
  
  // Move processInput outside useEffect to avoid stale closures
  const processInput = useCallback(async () => {
    const text = dslInput.trim();
    if (!text) {
      setLatexOutput([]);
      setError('');
      return;
    }

    setIsProcessing(true);
    setError('');
  
    try {
      // First preprocess the entire input to handle curly brackets
      const preprocessedText = preprocessDSLInput(text);
      console.log('Original input:', text);
      console.log('Preprocessed input:', preprocessedText);
      
      // Then split into lines
      const lines = preprocessedText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      console.log('Lines to process:', lines);
      const results = [];
  
      for (let i = 0; i < lines.length; i++) {
        try {
          console.log(`Processing line ${i + 1}:`, lines[i]);
          const latex = await (isLiveMode ? sendToLiveBackend(lines[i]) : sendToMockBackend(lines[i]));
          console.log(`LaTeX result for line ${i + 1}:`, latex);
          
          if (latex && typeof latex === 'string') {
            results.push({ latex, error: null });
          } else if (latex && typeof latex === 'object' && latex.type && 
              (latex.type === 'plot2d' || latex.type === 'plot3d' || latex.type === 'vectorfield')) {
            console.log('✅ PLOT DATA DETECTED');
            results.push({ plotData: latex, error: null });
          } else {
            results.push({ latex: String(latex), error: null });
          }
        } catch (err) {
          console.error(`Error processing line ${i + 1}:`, err);
          results.push({ latex: null, error: `Line ${i + 1}: ${err.message}` });
        }
      }
  
      console.log('Final results:', results);
      setLatexOutput(results);
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [dslInput, isLiveMode]);

  useEffect(() => {
    // Increase debounce delay from 300ms to 500ms for better performance
    const debounceTimer = setTimeout(processInput, 500);
    return () => clearTimeout(debounceTimer);
  }, [dslInput, isLiveMode]);

  if (isProcessing) {
    return <div className="status processing">🔄 Processing expressions...</div>;
  }

  if (error) {
    return <div className="status error">❌ {error}</div>;
  }

  if (latexOutput.length === 0) {
    return <div className="status">Type a formula to see LaTeX rendering...</div>;
  }

  return (
    <ErrorBoundary fallback={<div className="status error">Error rendering math expressions</div>}>
      <MathJaxContext config={mathJaxConfig}>
        <div className="latex-container">
          {latexOutput.map((item, index) => (
            <div key={index} className={`latex-block ${item.error ? 'error-block' : ''}`}>
              {item.error ? (
                <div className="error-line">{item.error}</div>
              ) : item.plotData ? (
                <div className="plot-content">
                  {item.plotData.type === 'plot2d' && <Plot2D data={item.plotData.data} metadata={item.plotData.metadata} />}
                  {item.plotData.type === 'plot3d' && <Plot3D data={item.plotData.data} metadata={item.plotData.metadata} />}
                  {item.plotData.type === 'vectorfield' && <VectorField data={item.plotData.data} metadata={item.plotData.metadata} />}
                </div>
              ) : (item.latex && (item.latex.startsWith('GRAPH_JSON_START') || item.latex.startsWith('CYCLE_START'))) ? (
                <div className="graph-content" style={{width: '100%', height: '70vh', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden'}}>
                  <iframe 
                    src={`/graphtheory.html?data=${encodeURIComponent(item.latex)}`}
                    style={{width: '100%', height: '100%', border: 'none'}}
                    title="Graph Visualization"
                  />
                </div>
              ) : (
                <div className="latex-content">
                  <MathJax>{`$$${item.latex}$$`}</MathJax>
                </div>
              )}
            </div>
          ))}
        </div>
      </MathJaxContext>
    </ErrorBoundary>
  );
};

export default LatexRenderer;